"use client"

import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import type { Gender } from "@/lib/quiz/types"

interface Props {
  onSelect: (gender: Gender) => void
  isFocus?: boolean
}

const OPTIONS: Gender[] = ["male", "female"]

export function GenderStep({ onSelect, isFocus = false }: Props) {
  const t = useTranslations("quiz")
  const [picked, setPicked] = useState<Gender | null>(null)
  // Freeze buttons only during the brief auto-advance animation so a
  // user returning from a later step can still re-pick. `picked` alone
  // would stay populated and lock the buttons forever.
  const [isAdvancing, setIsAdvancing] = useState(false)

  const handleClick = (g: Gender) => {
    if (isAdvancing) return
    setPicked(g)
    setIsAdvancing(true)
    // Hold the highlighted state briefly so the user sees their click land
    // before the wizard transitions to the next step.
    window.setTimeout(() => onSelect(g), 220)
  }

  return (
    <div
      className={
        "flex flex-col gap-3 sm:flex-row sm:flex-wrap" +
        (isFocus ? " sm:justify-center" : "")
      }
    >
      {OPTIONS.map((g) => {
        const isPicked = picked === g
        return (
          <button
            key={g}
            onClick={() => handleClick(g)}
            disabled={isAdvancing && !isPicked}
            className={
              "type-title-md cursor-pointer rounded-[12px] border px-6 py-4 text-left transition-colors disabled:cursor-not-allowed " +
              (isPicked
                ? "border-deep-blue-900 bg-deep-blue-900 text-white"
                : "hover:border-deep-blue-900 hover:bg-deep-blue-50 border-neutral-300 bg-white text-neutral-900 disabled:opacity-40")
            }
          >
            {t(`steps.gender.options.${g}`)}
          </button>
        )
      })}
    </div>
  )
}
